/**
 * A template being written: its own fields and the items it bundles.
 *
 * Shared with the template builder MCP app, so nothing here reaches for i18n,
 * the router or the query layer. Errors are reported as codes the caller turns
 * into words.
 */
import { z } from 'zod'

import type {
  Template,
  TemplateCandidate,
  TemplateCategoryId,
  TemplateCreate,
  TemplateUpdate,
} from '@/api/types'

export const TITLE_MAX_LENGTH = 160
export const NAME_MAX_LENGTH = 160

export interface DraftItem {
  id: number
  type: 'database' | 'dashboard'
  name: string
  description: string
  includeRows: boolean
  /** Carried because a selected dashboard reads from it, not chosen on its own. */
  required: boolean
}

export interface TemplateDraft {
  title: string
  description: string
  whenToUse: string
  categories: TemplateCategoryId[]
  items: DraftItem[]
}

export type DraftError = 'required' | 'tooLong'

export interface DraftErrors {
  title?: DraftError
  description?: DraftError
  whenToUse?: DraftError
  /** Keyed by the item's index in `items`. */
  items: Record<number, { name?: DraftError }>
}

const text = (max?: number) => {
  const required = z.string().trim().min(1, 'required')
  return max === undefined ? required : required.max(max, 'tooLong')
}

export const draftSchema = z.object({
  title: text(TITLE_MAX_LENGTH),
  description: text(),
  whenToUse: text(),
  categories: z.array(z.string()),
  items: z.array(z.object({ name: text(NAME_MAX_LENGTH) })).min(1),
})

export function validateDraft(draft: TemplateDraft): DraftErrors {
  const errors: DraftErrors = { items: {} }
  const result = draftSchema.safeParse(draft)
  if (result.success) return errors

  for (const issue of result.error.issues) {
    const code: DraftError = issue.message === 'tooLong' ? 'tooLong' : 'required'
    const [first, index, key] = issue.path
    if (first === 'items' && typeof index === 'number' && key === 'name') {
      errors.items[index] = { name: code }
    } else if (first === 'title' || first === 'description' || first === 'whenToUse') {
      errors[first] = code
    }
  }
  return errors
}

export function hasErrors(errors: DraftErrors): boolean {
  return (
    errors.title !== undefined ||
    errors.description !== undefined ||
    errors.whenToUse !== undefined ||
    Object.keys(errors.items).length > 0
  )
}

/** The first item with something to fix, so the pager can open on it. */
export function firstInvalidItem(errors: DraftErrors): number | undefined {
  const indexes = Object.keys(errors.items).map(Number)
  return indexes.length === 0 ? undefined : Math.min(...indexes)
}

/** The databases the selected dashboards read, which come along with them. */
export function requiredIds(
  selected: readonly number[],
  candidates: readonly TemplateCandidate[],
): Set<number> {
  const chosen = new Set(selected)
  return new Set(
    candidates
      .filter((candidate) => chosen.has(candidate.id))
      .flatMap((candidate) => candidate.required_application_ids),
  )
}

/**
 * A new draft of the selected applications and the databases they need, in the
 * order the workspace lists them.
 */
export function draftFromSelection(
  selected: readonly number[],
  candidates: readonly TemplateCandidate[],
): TemplateDraft {
  const chosen = new Set(selected)
  const required = requiredIds(selected, candidates)
  return {
    title: '',
    description: '',
    whenToUse: '',
    categories: [],
    items: candidates
      .filter((candidate) => chosen.has(candidate.id) || required.has(candidate.id))
      .map((candidate) => ({
        id: candidate.id,
        type: candidate.type,
        name: candidate.name,
        description: candidate.description,
        includeRows: false,
        required: required.has(candidate.id),
      })),
  }
}

export function draftFromTemplate(template: Template): TemplateDraft {
  return {
    title: template.title,
    description: template.description,
    whenToUse: template.when_to_use,
    categories: template.categories as TemplateCategoryId[],
    items: template.items.map((item) => ({
      id: item.id,
      type: item.type,
      name: item.name,
      description: item.description,
      includeRows: item.include_rows,
      required: item.required,
    })),
  }
}

export function toUpdateBody(draft: TemplateDraft): TemplateUpdate {
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    when_to_use: draft.whenToUse.trim(),
    categories: draft.categories,
    items: draft.items.map((item) => ({
      id: item.id,
      name: item.name.trim(),
      description: item.description.trim(),
    })),
  }
}

export function toCreateBody(draft: TemplateDraft): TemplateCreate {
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    when_to_use: draft.whenToUse.trim(),
    categories: draft.categories,
    items: draft.items.map((item) => ({
      application_id: item.id,
      name: item.name.trim(),
      description: item.description.trim(),
      include_rows: item.type === 'database' && item.includeRows,
    })),
  }
}
